import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button, GlassCard, SectionHeading } from '../components/ui';
import Seo from '../components/Seo';
import { getStaticMeta } from '../seo/pageMeta';

const getApiUrl = (endpoint) => {
  const baseUrl = process.env.REACT_APP_API_URL || 'https://alturion-backend-production.up.railway.app/api';
  return `${baseUrl}${endpoint}`;
};

const inputClassName =
  'w-full rounded-btn border border-white/10 bg-bg px-4 py-3 text-sm text-body placeholder:text-muted/70 transition-colors focus:border-white/25 focus:outline-none focus:ring-1 focus:ring-white/15';

const SupportScreen = () => {
  const { t, i18n } = useTranslation();
  
  // FAQ items avec traductions
  const getFaqItems = () => {
    const isEnglish = i18n.language === 'en';
    
    if (isEnglish) {
      return [
        {
          question: "How do I install my NinjaTrader indicators?",
          answer: "After your purchase, download the ZIP file from your profile. Open NinjaTrader 8, go to Tools > Import > NinjaScript Add-On, select the ZIP file and restart NinjaTrader."
        },
        {
          question: "How do I get my activation license?",
          answer: "Go to your profile, 'My Indicators' section, and click 'Get my license' for the relevant indicator. You will need to provide your signed NinjaTrader Machine ID."
        },
        {
          question: "What should I do if my indicator doesn't work?",
          answer: "First check that you have restarted NinjaTrader after installation. Also make sure you have the correct version of NinjaTrader (8.0 or higher). If the problem persists, contact our support."
        },
        {
          question: "Can I use my indicators on multiple computers?",
          answer: "Each license is tied to a specific Machine ID. To use your indicators on another computer, you will need to request a new license with that new computer's Machine ID."
        },
        {
          question: "How can I re-download my indicators?",
          answer: "You can download your indicators at any time from your profile, in the 'My Indicators' section. Download links remain active indefinitely."
        }
      ];
    } else {
      return [
        {
          question: "Comment installer mes indicateurs NinjaTrader ?",
          answer: "Après votre achat, téléchargez le fichier ZIP depuis votre profil. Ouvrez NinjaTrader 8, allez dans Tools > Import > NinjaScript Add-On, sélectionnez le fichier ZIP et redémarrez NinjaTrader."
        },
        {
          question: "Comment obtenir ma licence d'activation ?",
          answer: "Rendez-vous dans votre profil, section 'Mes Indicateurs', et cliquez sur 'Obtenir ma licence' pour l'indicateur concerné. Vous devrez fournir votre Machine ID signé NinjaTrader."
        },
        {
          question: "Que faire si mon indicateur ne fonctionne pas ?",
          answer: "Vérifiez d'abord que vous avez bien redémarré NinjaTrader après l'installation. Assurez-vous également d'avoir la bonne version de NinjaTrader (8.0 ou supérieur). Si le problème persiste, contactez notre support."
        },
        {
          question: "Puis-je utiliser mes indicateurs sur plusieurs ordinateurs ?",
          answer: "Chaque licence est liée à un Machine ID spécifique. Pour utiliser vos indicateurs sur un autre ordinateur, vous devrez demander une nouvelle licence avec le Machine ID de ce nouvel ordinateur."
        },
        {
          question: "Comment télécharger à nouveau mes indicateurs ?",
          answer: "Vous pouvez télécharger vos indicateurs à tout moment depuis votre profil, dans la section 'Mes Indicateurs'. Les liens de téléchargement restent actifs indéfiniment."
        }
      ];
    }
  };
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [contactStatus, setContactStatus] = useState({ loading: false, message: '', error: false });

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
      // Validation des champs
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      setContactStatus({ 
        loading: false, 
        message: t('home.contact.validation.required') || 'Tous les champs sont requis', 
        error: true 
      });
      return;
    }

    setContactStatus({ loading: true, message: '', error: false });

    try {
      const response = await axios.post(getApiUrl('/email/contact'), formData);
      
      if (response.data.success) {
        setContactStatus({ 
          loading: false, 
          message: response.data.message, 
          error: false 
        });
        // Réinitialiser le formulaire
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
      } else {
        throw new Error(response.data.message || 'Erreur lors de l\'envoi');
      }
    } catch (error) {
      console.error('Erreur envoi formulaire de contact:', error);
      setContactStatus({ 
        loading: false, 
        message: error.response?.data?.message || t('home.contact.validation.error') || 'Erreur lors de l\'envoi du message', 
        error: true 
      });
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-bg pt-28 pb-24 md:pt-32">
      <Seo
        title={getStaticMeta('support', i18n.language).title}
        description={getStaticMeta('support', i18n.language).description}
        path="/support"
      />
      <div className="mx-auto max-w-page px-6">
        <div className="mx-auto max-w-2xl text-center">
          <SectionHeading
            index="Support"
            title="Support"
            subtitle={
              i18n.language === 'en'
                ? 'Our team is here to help you with your indicators and account.'
                : 'Notre équipe vous aide pour vos indicateurs et votre compte.'
            }
            className="mb-14 [&>h2]:mx-auto [&>p]:mx-auto"
          />
        </div>

        <div className="mx-auto max-w-xl">
          <SectionHeading
            title={i18n.language === 'en' ? 'Contact us' : 'Contactez-nous'}
            subtitle={
              i18n.language === 'en'
                ? 'Fill out the form below and we will get back to you soon.'
                : 'Remplissez le formulaire — nous vous répondrons rapidement.'
            }
            className="mb-8 text-center [&>h2]:mx-auto [&>p]:mx-auto"
          />

          <GlassCard hover={false} className="p-6 md:p-8">
              <form onSubmit={handleContactSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-muted mb-2">
                      {i18n.language === 'en' ? 'Full name' : 'Nom complet'}
                    </label>
                    <input
                      type="text"
                      name="name"
                      placeholder={i18n.language === 'en' ? 'Your full name' : 'Votre nom complet'}
                      value={formData.name}
                      onChange={handleChange}
                      className={inputClassName}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-muted mb-2">
                      {i18n.language === 'en' ? 'Email address' : 'Adresse e-mail'}
                    </label>
                    <input
                      type="email"
                      name="email"
                      placeholder={i18n.language === 'en' ? 'your.email@example.com' : 'votre.email@exemple.com'}
                      value={formData.email}
                      onChange={handleChange}
                      className={inputClassName}
                      required
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-muted mb-2">
                    {i18n.language === 'en' ? 'Subject' : 'Sujet'}
                  </label>
                  <input
                    type="text"
                    name="subject"
                    placeholder={i18n.language === 'en' ? 'Subject of your request' : 'Objet de votre demande'}
                    value={formData.subject}
                    onChange={handleChange}
                    className={inputClassName}
                    required
                  />
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-muted mb-2">
                    {i18n.language === 'en' ? 'Message' : 'Message'}
                  </label>
                  <textarea
                    name="message"
                    placeholder={i18n.language === 'en' ? 'Describe your question or issue in detail...' : 'Décrivez votre question ou problème en détail...'}
                    value={formData.message}
                    onChange={handleChange}
                    rows="6"
                    className={`${inputClassName} resize-vertical`}
                    required
                  ></textarea>
                </div>

                <Button type="submit" disabled={contactStatus.loading} className="w-full">
                  {contactStatus.loading 
                    ? (i18n.language === 'en' ? 'Sending...' : 'Envoi...')
                    : (i18n.language === 'en' ? 'Send message' : 'Envoyer le message')
                  }
                </Button>

                {contactStatus.message && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`mt-6 p-4 rounded-btn text-sm text-center ${
                      contactStatus.error
                        ? 'bg-danger/10 border border-danger/20 text-danger'
                        : 'bg-success/10 border border-success/20 text-success'
                    }`}
                  >
                    {contactStatus.message}
                  </motion.div>
                )}
              </form>
          </GlassCard>
        </div>

        <div className="mx-auto mt-20 max-w-2xl">
          <SectionHeading
            title={i18n.language === 'en' ? 'FAQ' : 'FAQ'}
            subtitle={
              i18n.language === 'en'
                ? 'Quick answers to the most common questions.'
                : 'Réponses rapides aux questions les plus fréquentes.'
            }
            className="mb-8 text-center [&>h2]:mx-auto [&>p]:mx-auto"
          />

          <div className="divide-y divide-white/[.06] rounded-card border border-white/[.08] bg-surface">
            {getFaqItems().map((item, index) => (
              <details key={index} className="group">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-5 py-4">
                  <h3 className="text-left text-sm font-medium text-body md:text-base">
                    {item.question}
                  </h3>
                  <span className="shrink-0 text-muted transition-transform group-open:rotate-45">+</span>
                </summary>
                <div className="px-5 pb-5">
                  <p className="text-sm leading-relaxed text-muted">{item.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportScreen;
